import { type FC, type MouseEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styled from '@emotion/styled';
import { LinkText, Typography } from '@kizen/kds/Typography';
import {
  SizedCell,
  SortableHeadCell,
  TextEllipsisWithTooltip,
} from '__components/Kizen/Table';
import ActionCell from '__components/Tables/ActionCell';
import {
  AppNotificationIcon,
  useActionLabels,
} from 'pages/BusinessApps/subpages/Apps/columns';
import { BUSINESS_APP_ACTIONS } from 'ts-components/Plugins/businessApps/constants';
import { formatDateTime } from 'ts-components/Plugins/businessApps/helpers';
import type { BusinessAppAction } from 'ts-components/Plugins/businessApps/helpers';
import { APPS_TAB_PATHS } from '../../constants';
import { type InstalledAppRow, type InstalledAppType, issueUrl } from './rows';

const StyledInlineCell = styled.span`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

export const useTypeLabels = () => {
  const { t } = useTranslation();
  return useMemo(
    (): Record<InstalledAppType, string> => ({
      business: t('Business'),
      marketplace: t('Marketplace'),
    }),
    [t]
  );
};

type OnSelectAction = (
  action: { value: BusinessAppAction },
  row: InstalledAppRow
) => void;

const ActionsCell: FC<{
  data?: InstalledAppRow;
  onSelectAction: OnSelectAction;
}> = ({ data, onSelectAction, ...props }) => {
  const { t } = useTranslation();
  const labels = useActionLabels();
  const options = useMemo(
    () =>
      (data?.actions ?? []).map((value) => ({
        value,
        label:
          value === BUSINESS_APP_ACTIONS.useVersion
            ? t('Switch to Published Version')
            : labels[value],
      })),
    [data, labels, t]
  );

  if (!options.length) {
    return <SizedCell width="50px" padding="15px" />;
  }

  return (
    <ActionCell
      title={t('Select Action')}
      menuType="absolute"
      options={options}
      onSelectAction={onSelectAction}
      {...props}
      data={data}
      width="15px"
      padding="15px"
    />
  );
};

const headCell = (label: string, width: string, hasSorting = true) => (
  <SortableHeadCell
    label={label}
    width={width}
    padding="30px"
    hasSorting={hasSorting}
  />
);

export const useInstalledColumns = ({
  onSelectAction,
}: {
  onSelectAction: OnSelectAction;
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const typeLabels = useTypeLabels();

  return useMemo(
    () => [
      {
        id: 'spacer-start',
        headCell: <SizedCell width="15px" />,
        cell: <SizedCell width="15px" />,
        format: () => <span />,
      },
      {
        id: 'name',
        headCell: headCell(t('Name'), '240px'),
        cell: <SizedCell width="240px" padding="30px" />,
        format: (_: unknown, row: InstalledAppRow) => {
          const { detailsUrl } = row;
          if (!detailsUrl) {
            return (
              <TextEllipsisWithTooltip>{row.name}</TextEllipsisWithTooltip>
            );
          }
          return (
            <TextEllipsisWithTooltip
              type="anchor"
              href={detailsUrl}
              onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                e.preventDefault();
                history.push(detailsUrl, { from: history.location.pathname });
              }}
              data-qa-id={`installed-app-name-${row.apiName}`}
            >
              {row.name}
            </TextEllipsisWithTooltip>
          );
        },
      },
      {
        id: 'type',
        headCell: headCell(t('Type'), '130px'),
        cell: <SizedCell width="130px" padding="30px" />,
        format: (_: unknown, row: InstalledAppRow) => (
          <TextEllipsisWithTooltip>
            {typeLabels[row.type]}
          </TextEllipsisWithTooltip>
        ),
      },
      {
        id: 'status',
        headCell: headCell(t('Status'), '130px'),
        cell: <SizedCell width="130px" padding="30px" />,
        format: (_: unknown, row: InstalledAppRow) => (
          <StyledInlineCell>
            <Typography
              color={row.enabled ? 'feedback-success' : 'font-secondary'}
            >
              {row.enabled ? t('Enabled') : t('Disabled')}
            </Typography>
            {row.health && row.detailsUrl ? (
              <AppNotificationIcon
                detailsUrlFor={(subpath) => issueUrl(row, subpath)}
                notices={row.issues}
                severity={row.health}
                qaId={`installed-app-notifications-${row.apiName}`}
              />
            ) : null}
          </StyledInlineCell>
        ),
      },
      {
        id: 'version',
        headCell: headCell(t('Version'), '160px', false),
        cell: <SizedCell width="160px" padding="30px" />,
        format: (_: unknown, row: InstalledAppRow) => (
          <StyledInlineCell>
            <TextEllipsisWithTooltip>{row.version}</TextEllipsisWithTooltip>
            {row.usingPreview ? (
              <LinkText
                size="sm"
                href={APPS_TAB_PATHS.develop}
                onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                  e.preventDefault();
                  history.push(APPS_TAB_PATHS.develop);
                }}
                qa={{ id: `installed-app-using-preview-${row.apiName}` }}
              >
                {t('Using preview')}
              </LinkText>
            ) : null}
          </StyledInlineCell>
        ),
      },
      {
        id: 'updated',
        headCell: (
          <SortableHeadCell
            label={t('Date Updated')}
            numeric
            width="160px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="160px" padding="30px" />,
        format: (_: unknown, row: InstalledAppRow) => (
          <TextEllipsisWithTooltip>
            {formatDateTime(t, row.updated)}
          </TextEllipsisWithTooltip>
        ),
      },
      {
        id: 'published_by',
        headCell: headCell(t('Published By'), '180px'),
        cell: <SizedCell width="180px" padding="30px" />,
        format: (_: unknown, row: InstalledAppRow) => (
          <TextEllipsisWithTooltip>{row.publishedBy}</TextEllipsisWithTooltip>
        ),
      },
      {
        id: 'actions',
        headCell: (
          <SortableHeadCell
            hasSorting={false}
            label={t('Actions')}
            width="110px"
            padding="15px"
            align="right"
          />
        ),
        cell: (
          <ActionsCell
            onSelectAction={onSelectAction}
            data-qa-id="installed-app-row-actions"
          />
        ),
      },
    ],
    [t, history, typeLabels, onSelectAction]
  );
};
